<template>
  <div class="applicants-table-wrapper">
    <div v-if="loading" class="loading">로딩 중...</div>
    <template v-else>
      <div v-if="applicants.length === 0" class="no-applicants">신청자가 없습니다.</div>
      <div v-else>
        <div class="applicant-table-header">
          <h3>신청자 목록</h3>
            <div class="header-actions">
                <button
                    v-if="hasChanges"
                    @click="saveSelectionStatus"
                    class="save-button"
                    :disabled="hasInvalidReserveNumber"
                >변경사항 저장</button>
                <button @click="downloadApplicantsExcel" class="excel-button">엑셀 다운로드</button>
                </div>
                <div v-if="hasInvalidReserveNumber" style="color:#e02d2d; margin-top:4px;">
                모든 예비 당첨자의 예비 순번을 입력하세요.
            </div>
        </div>
        <!-- 검색 -->
        <div class="search-container">
          <select v-model="searchType" class="search-select">
            <option value="name">이름</option>
            <option value="phoneNumber">전화번호</option>
          </select>
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="searchType === 'name' ? '이름으로 검색' : '전화번호로 검색'"
            class="search-input"
            @keyup.enter="handleSearch"
          />
          <button @click="handleSearch" class="search-button">검색</button>
          <button v-if="isSearchActive" @click="clearSearch" class="clear-button">초기화</button>
        </div>
        <!-- 신청자 테이블 -->
        <table>
          <thead>
            <tr>
              <th>이름</th>
              <th>전화번호</th>
              <th>신청유형</th>
              <th>신청일</th>
              <th>당첨상태</th>
              <th>예비순번</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in paginatedApplicants" :key="a.id">
              <td>{{ a.name }}</td>
              <td>{{ a.phoneNumber }}</td>
              <td>{{ mapSupplyTypeLabel(a.subscriptionType) }}</td>
              <td>{{ formatDate(a.createdAt) }}</td>
              <td>
                <select
                  v-model="a.displayStatus"
                  class="selection-status"
                  :class="getStatusClass(a.displayStatus)"
                  @change="handleStatusChange(a.id, $event.target.value)"
                >
                  <option value="당첨">당첨</option>
                  <option value="예비">예비</option>
                  <option value="미당첨">미당첨</option>
                </select>
              </td>
              <td>
                <div v-if="a.displayStatus === '예비'" class="reserve-number-container">
                  <input
                    type="number"
                    v-model="a.reserveNumber"
                    class="reserve-number-input"
                    min="1"
                    @change="handleReserveNumberChange(a.id, $event.target.value)"
                    placeholder="순번"
                  />
                </div>
                <div v-else class="reserve-number-placeholder">
                  {{ a.displayStatus === '당첨' ? '-' : '' }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="pagination" v-if="totalPages > 1">
          <button :disabled="currentPage === 1" @click="currentPage--">이전</button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button :disabled="currentPage === totalPages" @click="currentPage++">다음</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import axios from '@/utils/axios'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

// Prop으로 subscriptionId 받음
const props = defineProps({
  subscriptionId: {
    type: [Number, String],
    required: true,
  }
})

const loading = ref(false)
const applicants = ref([])
const filteredApplicants = ref([])
const searchQuery = ref('')
const searchType = ref('name')
const isSearchActive = ref(false)
const hasChanges = ref(false)
const changedStatuses = ref({})
const currentPage = ref(1)
const itemsPerPage = 10

// 신청자 불러오기
const fetchApplicants = async (id) => {
  loading.value = true
  try {
    const res = await axios.get(`/bx201/subscriptionApplicants/by-subscription/${id}`)
    const applicantData = Array.isArray(res.data) ? res.data : []
    applicants.value = applicantData.map(applicant => {
      const { displayStatus, reserveNumber } = parseWinningState(applicant.winningState || '')
      return {
        ...applicant,
        originalWinningState: applicant.winningState || '',
        displayStatus,
        reserveNumber
      }
    })
    filteredApplicants.value = []
    isSearchActive.value = false
    searchQuery.value = ''
    currentPage.value = 1
    hasChanges.value = false
    changedStatuses.value = {}
  } finally {
    loading.value = false
  }
}

// 당첨상태 파싱
const parseWinningState = (winningState) => {
  if (!winningState) return { displayStatus: '미당첨', reserveNumber: null }
  if (winningState.startsWith('예비') && winningState.length > 2) {
    const reserveNumber = parseInt(winningState.substring(2), 10)
    if (!isNaN(reserveNumber)) return { displayStatus: '예비', reserveNumber }
  }
  if (winningState === '당첨') return { displayStatus: '당첨', reserveNumber: null }
  return { displayStatus: '미당첨', reserveNumber: null }
}
const combineWinningState = (displayStatus, reserveNumber) => {
  if (displayStatus === '예비' && reserveNumber) return `예비${reserveNumber}`
  return displayStatus
}

// 검색
const handleSearch = () => {
  if (!searchQuery.value.trim()) {
    clearSearch()
    return
  }
  const query = searchQuery.value.toLowerCase()
  filteredApplicants.value = applicants.value.filter(applicant => {
    if (searchType.value === 'name') {
      return applicant.name.toLowerCase().includes(query)
    } else {
      return applicant.phoneNumber.includes(query)
    }
  })
  isSearchActive.value = true
  currentPage.value = 1
}
const clearSearch = () => {
  searchQuery.value = ''
  isSearchActive.value = false
  currentPage.value = 1
}

// 상태 변경
const handleStatusChange = (applicantId, newStatus) => {
  const applicant = applicants.value.find(a => a.id === applicantId)
  if (!applicant) return
  applicant.displayStatus = newStatus
  if (newStatus !== '예비') applicant.reserveNumber = null
  const combinedState = combineWinningState(newStatus, applicant.reserveNumber)
  if (combinedState !== applicant.originalWinningState) {
    changedStatuses.value[applicantId] = combinedState
    hasChanges.value = true
  } else {
    if (changedStatuses.value[applicantId]) {
      delete changedStatuses.value[applicantId]
      hasChanges.value = Object.keys(changedStatuses.value).length > 0
    }
  }
  // 검색 결과에도 반영
  if (isSearchActive.value) {
    const filteredApplicant = filteredApplicants.value.find(a => a.id === applicantId)
    if (filteredApplicant) {
      filteredApplicant.displayStatus = newStatus
      if (newStatus !== '예비') filteredApplicant.reserveNumber = null
    }
  }
}

const hasInvalidReserveNumber = computed(() => {
  return Object.entries(changedStatuses.value).some(([state]) => {
    if (typeof state === "string" && state.startsWith('예비')) {
      const num = parseInt(state.replace('예비', ''), 10)
      return isNaN(num) || num < 1
    }
    return false
  })
})

// 예비 순번 변경
const handleReserveNumberChange = (applicantId, newNumber) => {
  const numberValue = parseInt(newNumber, 10)
  if (isNaN(numberValue) || numberValue < 1) return
  const applicant = applicants.value.find(a => a.id === applicantId)
  if (!applicant || applicant.displayStatus !== '예비') return
  applicant.reserveNumber = numberValue
  const combinedState = combineWinningState('예비', numberValue)
  if (combinedState !== applicant.originalWinningState) {
    changedStatuses.value[applicantId] = combinedState
    hasChanges.value = true
  } else {
    if (changedStatuses.value[applicantId]) {
      delete changedStatuses.value[applicantId]
      hasChanges.value = Object.keys(changedStatuses.value).length > 0
    }
  }
  if (isSearchActive.value) {
    const filteredApplicant = filteredApplicants.value.find(a => a.id === applicantId)
    if (filteredApplicant) {
      filteredApplicant.reserveNumber = numberValue
    }
  }
}

// 상태 클래스
const getStatusClass = (status) => {
  switch (status) {
    case '당첨': return 'status-selected'
    case '예비': return 'status-reserve'
    case '미당첨': return 'status-failed'
    default: return 'status-failed'
  }
}

// 저장
const saveSelectionStatus = async () => {
  try {
    if (!props.subscriptionId || Object.keys(changedStatuses.value).length === 0) return
    // const reserveNumbers = applicants.value
    //   .filter(a => a.displayStatus === '예비')
    //   .map(a => a.reserveNumber)
    //   .filter(num => num !== null)
    //const uniqueNumbers = new Set(reserveNumbers)
    // if (reserveNumbers.length !== uniqueNumbers.size) {
    //   alert('예비 순번이 중복되었습니다. 중복된 순번을 수정하세요.')
    //   return
    // }
    await axios.put('/bx201/subscriptionApplicants/update-winning-state', {
      subscriptionId: props.subscriptionId,
      statusUpdates: Object.entries(changedStatuses.value).map(([id, state]) => ({
        applicantId: id,
        winningState: state
      }))
    })
    Object.entries(changedStatuses.value).forEach(([id, state]) => {
      const applicant = applicants.value.find(a => a.id === id)
      if (applicant) applicant.originalWinningState = state
    })
    alert('당첨 상태가 성공적으로 저장되었습니다.')
    hasChanges.value = false
    changedStatuses.value = {}
  } catch (error) {
    alert('당첨 상태 저장 중 오류가 발생했습니다.')
  }
}

// 페이징
const paginatedApplicants = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const applicantsToUse = isSearchActive.value ? filteredApplicants.value : applicants.value
  return applicantsToUse.slice(start, start + itemsPerPage)
})
const totalPages = computed(() => {
  const applicantsToUse = isSearchActive.value ? filteredApplicants.value : applicants.value
  return Math.ceil(applicantsToUse.length / itemsPerPage)
})

// 날짜/라벨
const formatDate = (d) => {
  const date = new Date(d)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
const mapSupplyTypeLabel = (type) => {
  switch (type) {
    case 'GENERAL-15': return '15평 청년 일반공급'
    case 'GENERAL-30': return '30평 청년 일반공급'
    case 'SPECIAL-15': return '15평 청년 특별공급'
    case 'SPECIAL-30': return '30평 청년&신혼부부 일반공급'
    default: return type
  }
}

// 엑셀 다운로드
const downloadApplicantsExcel = () => {
  if (applicants.value.length === 0) {
    alert('다운로드할 신청자 목록이 없습니다.')
    return
  }
  const dataToDownload = isSearchActive.value ? filteredApplicants.value : applicants.value
  const worksheetData = dataToDownload.map(a => {
    const data = {
      이름: a.name,
      연락처: a.phoneNumber,
      신청유형: mapSupplyTypeLabel(a.subscriptionType),
      신청일: formatDate(a.createdAt),
      당첨상태: a.displayStatus || '미당첨'
    }
    if (a.displayStatus === '예비') {
      data['예비순번'] = a.reserveNumber || '미지정'
    } else {
      data['예비순번'] = '-'
    }
    if (a.subscriptionType === 'SPECIAL-15') {
      data['소득기준'] = a.income || ''
      data['지역기준'] = a.regional || ''
      data['자동차 소지 유무'] = a.car === true ? '유' : (a.car === false ? '무' : '')
      data['자산기준'] = a.assetBasis === true ? 'O' : ''
    }
    return data
  })
  const worksheet = XLSX.utils.json_to_sheet(worksheetData)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, '신청자목록')
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
  saveAs(blob, '청약_신청자목록.xlsx')
}

// watch로 props.subscriptionId 바뀔 때마다 신청자 불러옴
watch(() => props.subscriptionId, async (newId) => {
  if (newId) await fetchApplicants(newId)
}, { immediate: true })
</script>

<style scoped>
.applicants-table-wrapper {
  background: #fafaff;
  border: 1px solid #e5e6e9;
  border-radius: 8px;
  margin: 24px 0;
  padding: 28px 20px 16px 20px;
}
/* 테이블이 너무 좁아지지 않게 */
.responsive-table,
.applicants-table-wrapper table {
  width: 100%;
  min-width: 540px; /* 💡 컬럼 많은 경우 모바일에서도 스크롤 */
  border-collapse: collapse;
}

.loading {
  padding: 30px 0;
  text-align: center;
}

.no-applicants {
  padding: 60px 0 40px 0;
  text-align: center;
  font-size: 1.2rem;
  color: #666;
}

.applicant-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.header-actions {
  display: flex;
  gap: 10px;
}

.header-actions button {
  min-width: 120px;
  height: 38px;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 6px;
  border: none;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.18s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  margin: 0;
  padding: 0 18px;
}

/* 저장: 초록, 비활성화시 연회색 */
.save-button {
  background: #22c55e;
  color: #fff;
}
.save-button:disabled {
  background: #c3e6cb;
  color: #fff;
  cursor: not-allowed;
}
.save-button:not(:disabled):hover {
  background: #16a34a;
}

/* 엑셀 다운로드: 파랑 */
.excel-button {
  background: #2563eb;
  color: #fff;
}
.excel-button:hover {
  background: #1d4ed8;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.96rem;
}
th, td {
  padding: 11px;
  border: 1px solid #ddd;
  text-align: center;
}
thead {
  background-color: #f5f5f5;
}
.search-container {
  display: flex;
  gap: 10px;
  margin: 16px 0 20px 0;
  align-items: center;
}
.search-select {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: white;
  font-size: 0.92rem;
  min-width: 100px;
}
.search-input {
  flex: 1;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
}
.search-button {
  background-color: #007bff;
  color: white;
}
.clear-button {
  background-color: #6c757d;
  color: white;
}
.selection-status {
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
  width: 100px;
}
.reserve-number-container {
  display: flex;
  justify-content: center;
}
.reserve-number-input {
  width: 70px;
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
  text-align: center;
}
.reserve-number-placeholder {
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.status-selected {
  background-color: #d4edda;
  color: #155724;
  border-color: #c3e6cb;
}
.status-reserve {
  background-color: #fff3cd;
  color: #856404;
  border-color: #ffeeba;
}
.status-failed {
  background-color: #f8d7da;
  color: #721c24;
  border-color: #f5c6cb;
}
.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
}
.pagination button {
  padding: 6px 12px;
  border: 1px solid #ccc;
  background-color: white;
  cursor: pointer;
}
.pagination button:disabled {
  color: #aaa;
  cursor: not-allowed;
}


@media (max-width: 768px) {
  th, td {
    max-width: 80px;
    min-width: 50px;
    font-size: 0.81rem;
    padding: 6px 2px;
    white-space: normal;   /* 모바일에서 줄바꿈 허용 */
    word-break: break-all; /* 영어/숫자도 줄바꿈 */
  }
  .search-container { flex-direction: column; align-items: stretch; }
  .search-select, .search-input, .search-button, .clear-button {
    width: 100%; margin-bottom: 8px;
  }
  .selection-status { width: 80px; padding: 4px 6px; }
  .reserve-number-input { width: 50px; padding: 4px 6px; }
  .header-actions { flex-direction: column; gap: 5px; }
  .responsive-table,
  .applicants-table-wrapper table {
    min-width: 410px;        /* 모바일에서 적절한 최소폭 */
    font-size: 0.86rem;
  }
}
</style>
